import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ContextualMenu} from '@Components/contextual-menu';
import {COLOR_OBJECT_IDS, ColorFillOption} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {useUpdateEffect} from 'ahooks';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

interface PosterEditorFancyTextColorsPanelProps {
  panelId: string;
}

export default function PosterEditorFancyTextColorsPanel({...props}: PosterEditorFancyTextColorsPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemColors = useGetKeyFromFancyTextItem('colors');
  const totalColors = activeItemColors ? activeItemColors.length : 0;
  const [showColorPanel, setShowColorPanel] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState(ColorFillOption.PRIMARY);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useEffect((): void => {
    checkIfModalNeedsToBeClosed();
  }, [activeItemId]);

  useEffect((): void => {
    if (!showColorPanel) {
      updateHeightInRedux();
    }
  }, [showColorPanel, totalColors]);

  const updateHeightInRedux = (): void => {
    if (activeItem?.isFancyText() && activeItemColors !== undefined) {
      const colors = activeItemColors.length;
      const height = 112 + (colors - 1) * 48;
      updateOptionsPanelHeight(props.panelId, `${height}px`);
    }
  };

  const checkIfModalNeedsToBeClosed = (): void => {
    if (!showColorPanel || !activeItem?.isFancyText() || activeItemColors === undefined) {
      return;
    }

    const indexOfObject = COLOR_OBJECT_IDS.indexOf(selectedColorId);
    const colorsLength = activeItemColors.length;

    if (indexOfObject >= colorsLength) {
      onClose();
    }
  };

  if (!activeItem?.isFancyText() || activeItemColors === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_colors')} />;
  };

  const getLabel = (index: number): string => {
    switch (index) {
      case 2:
        return window.i18next.t('pmwjs_color_two');
      case 3:
        return window.i18next.t('pmwjs_color_three');
      case 1:
      default:
        return window.i18next.t('pmwjs_color_one');
    }
  };

  const showColors = (): ReactElement[] => {
    return activeItemColors.map((color, index) => {
      return (
        <ContextualMenuColorFillOption
          id={`fancy-text-color-${COLOR_OBJECT_IDS[index]}`}
          key={`fancy-text-color-${COLOR_OBJECT_IDS[index]}`}
          label={getLabel(index + 1)}
          data={[
            {
              id: `fancy-text-color-${COLOR_OBJECT_IDS[index]}`,
              color: getColorObjectArrayFromFillColor([activeItemColors[index]]),
            },
          ]}
          onClick={(): void => {
            setShowColorPanel(true);
            setSelectedColorId(COLOR_OBJECT_IDS[index]);
          }}
        />
      );
    });
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="fancy-text-colors-mobile" contentClassName="spacing-m-0 spacing-p-t-1 spacing-p-b-4 spacing-p-l-4 spacing-p-r-4">
        {showColors()}
      </ContextualMenu>
    );
  };

  const getIndexFromId = (id: ColorFillOption): number => {
    return COLOR_OBJECT_IDS.indexOf(id);
  };

  const getFillObject = (index: number): FillObject => {
    return {
      fillType: FillTypes.SOLID,
      fillColor: [activeItemColors[index]],
    };
  };

  const prepareNewColorArray = (updatedColor: RGBA): RGBA[] => {
    const newColorArray: RGBA[] = [];
    const selectedIndex = COLOR_OBJECT_IDS.indexOf(selectedColorId);
    for (let index = 0; index < activeItemColors.length; index += 1) {
      if (index === selectedIndex) {
        newColorArray.push(updatedColor);
      } else {
        newColorArray.push(activeItemColors[index]);
      }
    }
    return newColorArray;
  };

  const onChangeColor = (color: RGBA[], undoable = true): void => {
    void activeItem.updateFromObject(
      {
        colors: prepareNewColorArray(color[0]),
      },
      {
        undoable,
        doInvalidate: undoable,
      }
    );
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        onBackButton={(): void => {
          setShowColorPanel(false);
        }}
        panelId={props.panelId}
        fillObject={getFillObject(getIndexFromId(selectedColorId))}
        onChangeColor={onChangeColor}
        onClose={onClose}
        isAlphaDisabled
      />
    );
  };

  const getPanels = (): ReactElement => {
    if (showColorPanel) {
      return getColorPanel();
    }

    return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
  };

  return getPanels();
}
